import { create } from "zustand";

import { HttpError, api } from "../api/rest";
import type { BookUpdate, Envelope } from "../api/types";
import { SCHEMA_VERSION } from "../api/types";
import { createBatcher } from "../lib/batch";
import type { AssetBook } from "./book";
import { applyDelta, emptyBook, fromSnapshot } from "./book";

/**
 * Живые стаканы по токенам.
 *
 * Здесь всё, чего нет в чистом редьюсере: склейка обновлений до кадра,
 * перезапрос снапшота с троттлингом, зонд тишины и пометка неподтверждённости
 * при разрыве сокета.
 *
 * Про троттлинг. Ключ book:<asset_id> переписывается трейдером раз в ~10 с
 * (MD_SNAPSHOT_TTL/3), поэтому разрыв seq при открытии рынка — норма, и
 * немедленный перезапрос ГАРАНТИРОВАННО вернул бы тот же самый номер. Цикл
 * «увидел разрыв — перечитал» без выдержки дал бы шторм запросов, лечащий
 * ровно ничего. Отсюда пол в RESYNC_MIN_MS и рост паузы до RESYNC_MAX_MS.
 *
 * Сходимость при этом доказуема: трейдер пишет ключ ПОСЛЕ опубликованных
 * дельт, так что на очередном тике таймера номер в ключе становится не меньше
 * применённого нами, снапшот принимается, а следующая дельта оказывается S+1 —
 * мы всё это время подписаны. Практический потолок — те же ~10–15 с.
 */

/** Не чаще одного запроса снапшота в этот интервал. */
const RESYNC_MIN_MS = 5_000;
/** Потолок паузы при повторных неудачах. */
const RESYNC_MAX_MS = 30_000;
/** Во сколько раз растёт пауза. */
const RESYNC_BACKOFF = 1.5;
/** Тишина дольше этого — повод перечитать ключ. */
const SILENCE_MS = 30_000;
/** Как часто проверяем тишину. */
const PROBE_MS = 5_000;
/** Сколько закрытых стаканов держим, прежде чем выбрасывать. */
const KEEP_BOOKS = 8;

interface Sync {
  inFlight: boolean;
  lastAt: number;
  backoffMs: number;
  open: boolean;
  touchedAt: number;
}

interface BooksState {
  books: Record<string, AssetBook>;
  ingest: (type: "book_snapshot" | "book_delta", u: BookUpdate) => void;
  open: (assetId: string, conditionId: string) => void;
  close: (assetId: string) => void;
  markAllStale: () => void;
}

const sync = new Map<string, Sync>();

function syncOf(assetId: string): Sync {
  let s = sync.get(assetId);
  if (!s) {
    s = { inFlight: false, lastAt: 0, backoffMs: RESYNC_MIN_MS, open: false, touchedAt: Date.now() };
    sync.set(assetId, s);
  }
  return s;
}

type Pending = { type: "book_snapshot" | "book_delta"; u: BookUpdate };

let pending: Pending[] = [];

const batcher = createBatcher(() => {
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];

  useBooks.setState((state) => {
    const books = { ...state.books };
    for (const { type, u } of batch) {
      const prev = books[u.asset_id] ?? emptyBook(u.asset_id, u.condition_id);
      books[u.asset_id] = type === "book_snapshot" ? fromSnapshot(prev, u) : applyDelta(prev, u);
    }
    return { books };
  });

  // Перезапрос решается ПОСЛЕ применения всей пачки: иначе один всплеск дельт
  // с разрывом породил бы столько запросов, сколько было сообщений.
  // Любой статус, кроме live, — повод попробовать снова: дельты по стакану
  // без опоры (waiting/absent/error) копятся в буфере и сами статус не меняют,
  // а частоту запросов ограничивает троттлинг внутри resync.
  for (const assetId of new Set(batch.map((p) => p.u.asset_id))) {
    const book = useBooks.getState().books[assetId];
    if (book && book.status !== "live") resync(assetId);
  }
});

/** Запрос снапшота с троттлингом и отсечкой одновременных запросов. */
function resync(assetId: string): void {
  const s = syncOf(assetId);
  if (s.inFlight) return;

  const now = Date.now();
  const wait = Math.max(0, s.lastAt + s.backoffMs - now);
  if (wait > 0) {
    // Ждать через таймер незачем: следующая дельта или зонд тишины придут сами
    // и попробуют снова. Так поток запросов остаётся ровным.
    return;
  }

  s.inFlight = true;
  s.lastAt = now;

  void api
    .book(assetId)
    .then((env: Envelope<BookUpdate>) => {
      // Неизвестная версия схемы игнорируется, как и на всех остальных стыках
      // (сокет, webapi, bus): во время выкатки нового формата уже открытой
      // вкладке нельзя скармливать несовместимый снапшот.
      if (env.v !== SCHEMA_VERSION) {
        console.warn("снапшот стакана с неизвестной версией схемы", env.v);
        s.backoffMs = Math.min(s.backoffMs * RESYNC_BACKOFF, RESYNC_MAX_MS);
        return;
      }
      applySnapshot(assetId, env.payload);
      const after = useBooks.getState().books[assetId];
      // Пауза сбрасывается, только если цепочка действительно сомкнулась.
      s.backoffMs = after?.status === "live" ? RESYNC_MIN_MS : Math.min(s.backoffMs * RESYNC_BACKOFF, RESYNC_MAX_MS);
    })
    .catch((err: unknown) => {
      s.backoffMs = Math.min(s.backoffMs * RESYNC_BACKOFF, RESYNC_MAX_MS);
      useBooks.setState((state) => {
        const prev = state.books[assetId];
        if (!prev) return state;
        // 404 — это не сбой, а состояние: ключ протух или трейдер не
        // подтвердил стакан по этому токену. «Нет данных» и «стакан пуст» —
        // разные вещи, и оператор обязан их различать.
        const absent = err instanceof HttpError && err.status === 404;
        return {
          books: {
            ...state.books,
            [assetId]: absent
              ? { ...prev, status: "absent", error: err.message }
              : { ...prev, status: prev.status === "live" ? "syncing" : prev.status, error: String(err) },
          },
        };
      });
    })
    .finally(() => {
      s.inFlight = false;
    });
}

function applySnapshot(assetId: string, u: BookUpdate): void {
  useBooks.setState((state) => {
    const prev = state.books[assetId] ?? emptyBook(assetId, u.condition_id);
    return { books: { ...state.books, [assetId]: fromSnapshot(prev, u) } };
  });
}

export const useBooks = create<BooksState>(() => ({
  books: {},

  ingest: (type, u) => {
    if (!u.asset_id) return;
    pending.push({ type, u });
    batcher.schedule();
  },

  open: (assetId, conditionId) => {
    const s = syncOf(assetId);
    s.open = true;
    s.touchedAt = Date.now();
    // Пауза сбрасывается: открытие рынка — это явное действие оператора,
    // а не повтор неудачи.
    s.backoffMs = RESYNC_MIN_MS;
    s.lastAt = 0;

    useBooks.setState((state) =>
      state.books[assetId]
        ? state
        : { books: { ...state.books, [assetId]: emptyBook(assetId, conditionId) } },
    );
    resync(assetId);
  },

  close: (assetId) => {
    const s = sync.get(assetId);
    if (s) {
      s.open = false;
      s.touchedAt = Date.now();
    }
    prune();
  },

  markAllStale: () => {
    // Разрыв сокета — это потерянные дельты по определению. Показывать
    // «подтверждён» на стакане, который мог измениться, пока связи не было,
    // значит уверенно врать.
    useBooks.setState((state) => {
      const books: Record<string, AssetBook> = {};
      for (const [id, b] of Object.entries(state.books)) {
        books[id] = b.status === "live" ? { ...b, status: "syncing" } : b;
      }
      return { books };
    });
  },
}));

/** Выбрасывает давно закрытые стаканы, чтобы вкладка не копила их сутками. */
function prune(): void {
  const closed = [...sync.entries()]
    .filter(([, s]) => !s.open)
    .sort((a, b) => b[1].touchedAt - a[1].touchedAt)
    .slice(KEEP_BOOKS)
    .map(([id]) => id);
  if (closed.length === 0) return;

  for (const id of closed) sync.delete(id);
  useBooks.setState((state) => {
    const books = { ...state.books };
    for (const id of closed) delete books[id];
    return { books };
  });
}

/**
 * Зонд тишины.
 *
 * Один и тот же путь честно различает два состояния, которые иначе выглядят
 * одинаково: на тихом, но живом рынке перезапрос вернёт снапшот с тем же seq,
 * статус станет live и предупреждение исчезнет; на умершем трейдере ключ уже
 * истёк, придёт 404, и лесенка покажет «нет данных» вместо застывшего леса.
 *
 * Зонд — ЕДИНСТВЕННЫЙ путь восстановления для waiting/absent/error: один
 * неудачный стартовый GET /book (рестарт web_api, ключ ещё не записан) иначе
 * оставлял бы «загрузку стакана…» навсегда — трейдер по своей воле снапшот в
 * канал не публикует, а open() выполняется один раз. Частоту ограничивает
 * троттлинг resync с его экспоненциальной паузой.
 */
if (typeof window !== "undefined") {
  window.setInterval(() => {
    const now = Date.now();
    const { books } = useBooks.getState();
    for (const [assetId, b] of Object.entries(books)) {
      if (!sync.get(assetId)?.open) continue;
      if (b.status !== "live" || now - b.appliedAt > SILENCE_MS) {
        resync(assetId);
      }
    }
  }, PROBE_MS);
}
